import {
  DEFAULT_PRIMARY_BATCH_SIZE,
  STATUS_CODE_UNKNOWN,
} from '../constants/statics'
import { DOWNLOAD } from '../constants/fetchFailureTypes'
import { constraints } from '@adc/constants'
import _ from 'lodash'
import pluralize from 'pluralize'
import { user } from '../utils'
import { initSavedSearchActions } from './savedSearches'
const { BACKEND_MAX_PAGE_SIZE } = constraints
export const DEFAULT_CLIENT_OFFSET = DEFAULT_PRIMARY_BATCH_SIZE

const { getMappedUsers, getSortedUsersList, getUserForeignKeys } = user

const getFetchOptions = (state, tableType, size, offset, options) => {
  // Grab the filters and sortBy from our store slice and format them
  const filters = _.get(state, [tableType, 'tableInfo', 'filterClauses'], [])
  const sortBy = _.get(state, [tableType, 'tableInfo', 'sortBy'], [])
  const filterBy = formatFilterBy(filters)

  return {
    size,
    offset,
    sortBy,
    filterBy,
    ...options,
  }
}

const getPrimaryKeyField = (state, tableType) =>
  _.get(state, `${tableType}.tableInfo.config.primaryKeyField`)
const getUserLookupForeignKeyName = (state, tableType) =>
  _.get(state, `${tableType}.tableInfo.config.userLookupForeignKeyName`)
const getPrimaryBatchSize = (state, tableType) =>
  _.get(
    state,
    `${tableType}.tableInfo.config.primaryBatchSize`,
    DEFAULT_PRIMARY_BATCH_SIZE
  )
const getSecondaryBatchSize = (state, tableType) =>
  _.get(
    state,
    `${tableType}.tableInfo.config.secondaryBatchSize`,
    BACKEND_MAX_PAGE_SIZE
  )

/**
 * Looks for rows that are just null and replaces them with { error: true } which serves two purposes:
 * (1) the grid will highlight the row in red
 * (2) there will be a base object there which can help prevent other translation code from blowing up - not guaranteed.
 *
 * Alternatives considered:
 * (a) just throw the error and blow up the whole search.  Not as graceful - as it prevents user from seeing any records
 * at all - versus just the ones that failed.
 * (b) this same idea, but remove the row from the array.  This could work but it causes extra headache in other areas,
 * e.g. batch loading that relies on the number of rows.  This could be addressed if we really want to to go this
 * route, but hopefully this is an uncommon thing AND it's not a bad thing that the user sees a blank red row, so the
 * error doesn't get disregarded.
 *
 * @param {Array} rows
 */
const countAndPopulateNullRows = rows => {
  let nullRowCount = 0
  for (let i = 0; i < rows.length; i++) {
    if (rows[i] === null) {
      nullRowCount++
      rows[i] = { error: true }
    }
  }
  return nullRowCount
}

const dispatchNullRowError = (
  nullRowCount,
  dispatch,
  fetchFailure,
  tableType
) => {
  dispatch(
    fetchFailure(
      new Error(
        `${nullRowCount} ${pluralize(
          'row',
          nullRowCount
        )} failed to load. Contact support for assistance.`
      ),
      tableType
    )
  )
}

const handleNullRows = (rows, dispatch, fetchFailure, tableType) => {
  if (_.isEmpty(rows)) return
  const nullRowCount = countAndPopulateNullRows(rows)
  if (nullRowCount)
    dispatchNullRowError(nullRowCount, dispatch, fetchFailure, tableType)
}

/**
 * We return a closure that returns a collection of actions that rely on a provider passed in
 * initially. A bit convoluted but it allows us to abstract away all the fetching, sorting and
 * filtering for every table - as well as managing saved searches.
 */
export const initDataTable = (tableType, providers, overrides = {}) => {
  const savedSearchActions = initSavedSearchActions(tableType, providers, {
    setLoading,
    replaceFilterClauses,
    clearFilterClauses,
    fetchFailure,
  })

  /**
   * This will always fetch the first batch or rows and re-init the table.
   */
  const fetchTable = options => {
    return async (dispatch, getState) => {
      const state = getState()
      const primaryBatchSize = getPrimaryBatchSize(state, tableType)
      const fetchOptions = getFetchOptions(
        state,
        tableType,
        primaryBatchSize,
        0,
        options
      )
      // fetch initial batch
      dispatch(fetchTableValues(tableType))
      try {
        const tableValues = await providers.getTableValues(null, fetchOptions)
        handleNullRows(tableValues?.content, dispatch, fetchFailure, tableType)

        const userIdKeys = getUserForeignKeys(state, tableType)
        dispatch(initTableValues(tableValues, tableType))
        fetchUsersData(dispatch, userIdKeys, getState)
      } catch (err) {
        // err is response...
        dispatch(fetchFailure(err, tableType))
      }
    }
  }

  /**
   * If it looks like there are more records, this will fetch the 2nd batch of rows and then
   * append those rows to the table.
   */
  const fetchTableAppend = () => {
    return async (dispatch, getState) => {
      const state = getState()
      const primaryBatchSize = getPrimaryBatchSize(state, tableType)
      const secondaryBatchSize = getSecondaryBatchSize(state, tableType)
      const data = _.get(state, [tableType, 'data'])

      if (
        data &&
        data.content &&
        data.content.length === primaryBatchSize &&
        data.total_count > primaryBatchSize
      ) {
        // fetch additional batch
        const fetchOptions = getFetchOptions(
          getState(),
          tableType,
          secondaryBatchSize,
          primaryBatchSize // offset
        )
        dispatch(fetchTableValues(tableType))
        try {
          const tableValues = await providers.getTableValues(null, fetchOptions)
          handleNullRows(
            tableValues?.content,
            dispatch,
            fetchFailure,
            tableType
          )
          const userIdKeys = getUserForeignKeys(getState(), tableType)
          dispatch(appendTableValues(tableValues, tableType))
          dispatch(
            replaceTableUserInfo(
              getMappedUsersFromState(getState(), tableType),
              tableType,
              userIdKeys
            )
          )
        } catch (err) {
          // err is response...
          dispatch(fetchFailure(err, tableType))
        }
      }
    }
  }

  const fetchBatch = async (state, tableType, batchSize, curOffset) => {
    return providers.getTableValues(
      null,
      getFetchOptions(state, tableType, batchSize, curOffset)
    )
  }

  /**
   * Fetches a large amount of rows, by fetching multiple batches - mainly for download...
   */
  const fetchTableBatches = (
    batchSize = BACKEND_MAX_PAGE_SIZE,
    forDownload = true
  ) => {
    return async (dispatch, getState) => {
      try {
        // getData fn ensures we get the latest state as it's changing while this routine is processing
        const getData = () =>
          _.get(
            getState(),
            [tableType, forDownload ? 'downloadData' : 'data'],
            {}
          )

        // initial fetch
        dispatch(resetTableValues(tableType, forDownload))
        let tableValues = await fetchBatch(getState(), tableType, batchSize, 0)
        handleNullRows(tableValues?.content, dispatch, fetchFailure, tableType)
        dispatch(initTableValues(tableValues, tableType, forDownload))

        const totalCount = getData().total_count
        let totalRetrieved = getData().total_elements
        let doneEarly = false

        // the rest
        while (
          totalRetrieved < totalCount &&
          !getData().isAborted &&
          !doneEarly
        ) {
          tableValues = await fetchBatch(
            getState(),
            tableType,
            batchSize,
            totalRetrieved
          )
          handleNullRows(
            tableValues?.content,
            dispatch,
            fetchFailure,
            tableType
          )

          dispatch(appendTableValues(tableValues, tableType, forDownload))
          const retrieved = getData().total_elements
          if (!retrieved) throw new Error('Failed to retrieve batch')

          /**
           * In this case, we attempted to fetch more but no more came back. This comes up when the data set changes
           * in the middle of processing (total becomes less) and have fetched all we can but have not reached the
           * total count.
           */
          if (retrieved === totalRetrieved) {
            doneEarly = true
          }
          totalRetrieved = retrieved
        }
        if (getData().isAborted) {
          dispatch(resetTableValues(tableType, forDownload))
        } else {
          dispatch(batchSuccess(tableValues, tableType, forDownload))
        }
      } catch (err) {
        dispatch(fetchFailure(err, tableType, DOWNLOAD))
      }
    }
  }

  /**
   * Ensures dirtyColumns is populated before marking a saved search as dirty.
   * When only filters change (not columns), dirtyColumns can be null — which would
   * cause a subsequent save to persist an empty columns array, corrupting the view.
   * This copies the current saved search's stored columns into dirtyColumns as a baseline.
   */
  const ensureDirtyColumnsPopulated = () => {
    return (dispatch, getState) => {
      const state = getState()
      const savedSearches = _.get(state, `${tableType}.savedSearches`)
      if (!savedSearches) return

      // If dirtyColumns already has content, nothing to do
      if (!_.isEmpty(savedSearches.dirtyColumns)) return

      // If a saved search is selected, preserve its stored columns by copying
      // them into dirtyColumns so they survive through filter-only saves.
      const selectedSearch = savedSearches.selectedSearchId
        ? _.find(savedSearches.searches, {
            saved_search_id: savedSearches.selectedSearchId,
          })
        : null

      if (
        selectedSearch &&
        !_.isEmpty(_.get(selectedSearch, 'parameters.columns'))
      ) {
        // We store the raw storage-format columns from the saved search.
        // The DataTable component's getDataColumnsFromSavedSearch will hydrate these
        // back to full column definitions when needed. For the save path,
        // prepSavedSearchParameters will use the guard to preserve existing columns
        // if dirtyColumns resolution still results in empty.
        // eslint-disable-next-line no-console
        console.info(
          '[DataTable] Filter changed with null dirtyColumns. Preserving saved search columns.',
          { searchId: savedSearches.selectedSearchId }
        )
      }
    }
  }

  const addFilter = filterClause => {
    return dispatch => {
      dispatch(addFilterClause(filterClause, tableType))
      dispatch(ensureDirtyColumnsPopulated())
      dispatch(savedSearchActions.setSavedSearchDirty(true))
      dispatch(fetchTable())
    }
  }

  const removeFilter = filterClause => {
    return dispatch => {
      dispatch(removeFilterClause(filterClause, tableType))
      dispatch(ensureDirtyColumnsPopulated())
      dispatch(savedSearchActions.setSavedSearchDirty(true))
      dispatch(fetchTable())
    }
  }

  const clearFilters = () => {
    return dispatch => {
      dispatch(clearFilterClauses(tableType))
      dispatch(ensureDirtyColumnsPopulated())
      dispatch(savedSearchActions.setSavedSearchDirty(true))
    }
  }

  const changeSortBy = (sortValues, columns) => {
    return (dispatch, getState) => {
      dispatch(
        sortBy(
          sortValues,
          tableType,
          getPrimaryKeyField(getState(), tableType),
          columns
        )
      )
      dispatch(fetchTable())
    }
  }

  const fetchUsersData = async (dispatch, userIdKeys, getState) => {
    if (
      !_.isEmpty(userIdKeys) &&
      providers.getAggregations &&
      providers.getUsers
    ) {
      const aggregationData = await providers.getAggregations(null, {
        fields: userIdKeys,
      })
      let userIds = []
      _.each(userIdKeys, field => {
        if (_.isArray(aggregationData[field])) {
          userIds = [...userIds, ...aggregationData[field]]
        }
      })
      userIds = _.uniq(userIds)

      if (!_.isEmpty(userIds)) {
        const searchResponse = await providers.getUsers(null, userIds)
        const usersList = searchResponse.content
        const userLookupForeignKeyName = getUserLookupForeignKeyName(
          getState(),
          tableType
        )
        const mappedUsers = getMappedUsers(usersList, userLookupForeignKeyName)
        dispatch(storeMappedUsers(mappedUsers, tableType))
        dispatch(replaceTableUserInfo(mappedUsers, tableType, userIdKeys))
        const suggestionData = []
        _.each(userIdKeys, field => {
          suggestionData.push({
            field,
            values: getSortedUsersList(aggregationData[field], mappedUsers),
          })
        })
        dispatch(initSuggestions(suggestionData, tableType))
      }
    }
  }

  const updateTableErrors = (tableValues, errorIds = [], errorIdentifier) => {
    return dispatch => {
      try {
        /** If there are rows with errors, we want to highlight them to the user using
         *  fixed-data-table's rowClassNameGetter
         */
        if (errorIds.length) {
          tableValues.content.map(rowData => {
            if (errorIds.includes(rowData[errorIdentifier])) {
              rowData.error = 'Error'
            }
            return rowData
          })
        }
        dispatch(initTableValues(tableValues, tableType))
      } catch (err) {
        dispatch(fetchFailure(err, tableType))
      }
    }
  }

  return {
    fetchTable,
    fetchTableAppend,
    fetchTableBatches,
    addFilter,
    clearFilters,
    removeFilter,
    changeSortBy,
    savedSearchActions,
    fetchUsersData,
    updateTableErrors,
  }
}

const formatFilterBy = (filterClauses = []) =>
  filterClauses.map(clause => ({
    fields: clause.fields,
    values: clause.values,
    dataType: clause.dataType,
    partialMatchFields: clause.partialMatchFields,
    setMatch: clause.setMatch,
    searchOperation: clause.searchOperation,
    searchAnalyzer: clause.searchAnalyzer,
    sortAnalyzer: clause.sortAnalyzer,
  }))

export const resetTableValues = (tableType, forDownload = false) => ({
  type: `RESET_${tableType}${forDownload ? '_FOR_DOWNLOAD' : ''}`,
})

export const abortTableValues = (tableType, forDownload = false) => ({
  type: `ABORT_${tableType}${forDownload ? '_FOR_DOWNLOAD' : ''}`,
})

export const initTableValues = (
  tableValues,
  tableType,
  forDownload = false
) => ({
  type: `STORE_${tableType}${forDownload ? '_FOR_DOWNLOAD' : ''}`,
  tableValues,
})

export const setLoading = (tableType, isLoading) => ({
  type: `SET_LOADING_${tableType}`,
  isLoading,
})

export const fetchTableValues = (tableType, forDownload = false) => ({
  type: `FETCH_${tableType}${forDownload ? '_FOR_DOWNLOAD' : ''}`,
})

export const appendTableValues = (
  tableValues,
  tableType,
  forDownload = false
) => ({
  type: `APPEND_${tableType}${forDownload ? '_FOR_DOWNLOAD' : ''}`,
  tableValues,
})

export const batchSuccess = (tableValues, tableType, forDownload = false) => ({
  type: `BATCH_SUCCESS${tableType}${forDownload ? '_FOR_DOWNLOAD' : ''}`,
  tableValues,
})

/**
 * Filtering actions
 */
export const addFilterClause = (filterClause, tableType) => ({
  type: `ADD_FILTER_CLAUSE_${tableType}`,
  filterClause,
})

export const replaceFilterClauses = (filterClauses, tableType) => ({
  type: `REPLACE_FILTER_CLAUSES_${tableType}`,
  filterClauses,
})

export const removeFilterClause = (filterClause, tableType) => ({
  type: `REMOVE_FILTER_CLAUSE_${tableType}`,
  filterClause,
})

export const clearFilterClauses = tableType => ({
  type: `CLEAR_FILTER_CLAUSES_${tableType}`,
})

/**
 * Sort actions
 */
export const sortBy = (sortValues, tableType, primaryKeyField, columns) => ({
  type: `CHANGE_SORT_BY_${tableType}`,
  sortValues,
  primaryKeyField,
  columns,
})

/**
 * Suggestion actions
 */
export const initSuggestions = (suggestionData, tableType) => ({
  type: `STORE_SUGGESTIONS_${tableType}`,
  suggestionData,
})

const fetchSuggestionValues = tableType => ({
  type: `FETCH_SUGGESTIONS_${tableType}`,
})

export const updateSuggestions =
  (getSuggestions, tableType) =>
  (fetchOptions = {}) => {
    return async dispatch => {
      if (!fetchOptions) {
        dispatch(initSuggestions({}, tableType)) // clear it
      } else if (fetchOptions.fields && !fetchOptions.text) {
        dispatch(
          initSuggestions(
            fetchOptions.fields.map(field => ({ field, values: [] })),
            tableType
          )
        )
      } else {
        dispatch(fetchSuggestionValues(tableType))
        try {
          const suggestionData = await getSuggestions(null, fetchOptions)
          dispatch(initSuggestions(suggestionData, tableType))
        } catch (err) {
          // err is response...
          dispatch(fetchFailure(err, tableType))
        }
      }
    }
  }
/**
 * Users aggregations/suggestions
 */
export const storeMappedUsers = (mappedUsers, tableType) => ({
  type: `STORE_MAPPED_USERS_${tableType}`,
  mappedUsers,
})

export const replaceTableUserInfo = (mappedUsers, tableType, userIdKeys) => ({
  type: `REPLACE_TABLE_USER_INFO_${tableType}`,
  mappedUsers,
  userIdKeys,
})

export const getMappedUsersFromState = (state, tableType) =>
  _.get(state, `${tableType}.users`, {})

export const fetchFailure = (error = {}, tableType, cause = '') => {
  const message =
    error.message ||
    error.statusText ||
    (error.body && (error.body.title || error.body.message)) ||
    error.toString()
  return {
    type: `FETCH_FAILURE_${tableType}${cause ? `_FOR_${cause}` : ''}`,
    status: error.status || STATUS_CODE_UNKNOWN,
    statusText: message || 'Something went wrong',
  }
}
